import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useInstagramCore } from "../instagram";
import { useInstagramLoginCore } from "../instagramLogin";
import { useMeCore } from "../me";
import { resolveInstagramSocialSetting } from "../socialPost/config";

export interface InstagramDisplayProfile {
  name?: string;
  username?: string;
  avatarUrl?: string;
}

export function toInstagramDisplayProfile(settings: unknown): InstagramDisplayProfile {
  if (!settings || typeof settings !== "object") return {};
  const s = settings as Record<string, unknown>;
  return {
    name: typeof s.name === "string" ? s.name : undefined,
    username: typeof s.username === "string" ? s.username : undefined,
    avatarUrl:
      typeof s.avatarUrl === "string"
        ? s.avatarUrl
        : typeof s.avatar === "string"
          ? s.avatar
          : undefined,
  };
}

export function formatInstagramDisplayName(profile: InstagramDisplayProfile): string {
  const raw = profile.name || profile.username;
  if (!raw) return "";
  if (raw.startsWith("@")) return raw;
  if (profile.username) return `@${profile.username}`;
  return raw;
}

/** Settings de cuenta Instagram activa (Facebook path, Login path o me/socialSettings). */
export function useInstagramDisplaySettings(): InstagramDisplayProfile {
  const facebookPath = useInstagramCore();
  const loginPath = useInstagramLoginCore();
  const { socialSettings } = useMeCore();

  return useMemo(() => {
    if (!_.isEmpty(facebookPath.settings)) {
      return toInstagramDisplayProfile(facebookPath.settings);
    }
    if (!_.isEmpty(loginPath.settings?.token)) {
      return toInstagramDisplayProfile(loginPath.settings);
    }
    return toInstagramDisplayProfile(
      resolveInstagramSocialSetting(
        socialSettings as Record<string, unknown> | null | undefined
      )
    );
  }, [facebookPath.settings, loginPath.settings, socialSettings]);
}

/**
 * Enruta publicaciones de Instagram al flujo activo:
 * - Facebook + Page (multiposteador actual) tiene prioridad
 * - Si no, usa Instagram Login (sin Facebook)
 */
export function useInstagramPublishRouter() {
  const intl = useIntl();
  const facebookPath = useInstagramCore();
  const loginPath = useInstagramLoginCore();
  const { socialSettings } = useMeCore();

  const rejectStoriesOnLoginPath = useCallback(() => {
    toast.warn(
      intl.formatMessage({ id: "app.instagramLogin.storiesRequireFacebook" })
    );
    return Promise.resolve();
  }, [intl]);

  const loginSettings = useMemo(() => {
    if (!_.isEmpty(loginPath.settings?.token)) return loginPath.settings;
    const viaLogin = socialSettings?.instagramLogin;
    if (viaLogin?.token) return viaLogin;
    return loginPath.settings;
  }, [loginPath.settings, socialSettings]);

  const usesLoginPath = useMemo(() => {
    if (!_.isEmpty(facebookPath.settings)) return false;
    return !_.isEmpty(loginSettings?.token);
  }, [facebookPath.settings, loginSettings]);

  return useMemo(
    () =>
      usesLoginPath
        ? {
            usesLoginPath: true,
            settings: loginSettings,
            errorCode: null,
            errorMessage: null,
            socialPost: loginPath.socialPost,
            socialPostVideo: loginPath.socialPostVideo,
            modifySocialPost: loginPath.modifySocialPost,
            modifySocialPostVideo: loginPath.modifySocialPostVideo,
            socialStory: rejectStoriesOnLoginPath,
            socialStoryVideo: rejectStoriesOnLoginPath,
            modifySocialStory: rejectStoriesOnLoginPath,
            modifySocialStoryVideo: rejectStoriesOnLoginPath,
            cleanErrors: facebookPath.cleanErrors,
          }
        : {
            usesLoginPath: false,
            settings: facebookPath.settings,
            errorCode: facebookPath.errorCode,
            errorMessage: facebookPath.errorMessage,
            socialPost: facebookPath.socialPost,
            socialPostVideo: facebookPath.socialPostVideo,
            modifySocialPost: facebookPath.modifySocialPost,
            modifySocialPostVideo: facebookPath.modifySocialPostVideo,
            socialStory: facebookPath.socialStory,
            socialStoryVideo: facebookPath.socialStoryVideo,
            modifySocialStory: facebookPath.modifySocialStory,
            modifySocialStoryVideo: facebookPath.modifySocialStoryVideo,
            cleanErrors: facebookPath.cleanErrors,
          },
    [usesLoginPath, facebookPath, loginPath, loginSettings, rejectStoriesOnLoginPath]
  );
}
